import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { SfoUiJSONSchema7 } from '../../metadata.model';
import { SchemaKeywords, SfoUiWidgetType } from '../types';
import { pickObjectVariantForValue, resolveObjectSchemaForService } from './form-build-helpers';
import { buildDateVariableFormGroup } from './form-csl-helpers';
import { buildNumberValidators, buildStringValidators } from './form-validators';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private _advancedMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly advancedMode$ = this._advancedMode$.asObservable();

  constructor(private formBuilder: FormBuilder) {}

  /**
   * Toggles the advanced mode between true and false.
   */
  toggleAdvancedMode(): void {
    this._advancedMode$.next(!this._advancedMode$.value);
  }

  generateForm(schema: SfoUiJSONSchema7): FormGroup {
    if (!schema) {
      throw new Error('No schema provided');
    }

    if (!schema['properties']) {
      throw new Error('Invalid schema provided. Properties are missing');
    }

    return this.buildFormGroup(schema);
  }

  buildFormGroup(schema: SfoUiJSONSchema7, prefix = '', defaults?: unknown): FormGroup {
    const baseForm: FormGroup = this.formBuilder.group({});

    // Resolve union object schemas to a concrete object variant for group building
    const resolvedForGroup: SfoUiJSONSchema7 = resolveObjectSchemaForService(schema) || schema;
    const properties: Record<string, SfoUiJSONSchema7> =
      (resolvedForGroup.properties as Record<string, SfoUiJSONSchema7>) ||
      (schema as unknown as Record<string, SfoUiJSONSchema7>);

    for (const key of Object.keys(properties)) {
      const property: SfoUiJSONSchema7 = properties[key];
      const fullKey = prefix ? `${prefix}.${key}` : key; // get total key path

      if (property.const !== undefined) {
        baseForm.addControl(key, this.formBuilder.control(property.const));
        continue;
      }

      if (!property.type) {
        if (property['anyOf'] || property['oneOf']) {
          // Special handling for date-variable fields
          if (property._ui_widget === 'sfo-date-variable-input') {
            baseForm.addControl(key, buildDateVariableFormGroup(this.formBuilder));
          } else {
            baseForm.addControl(key, this.buildUnionFormControl(property, fullKey));
          }
        }
      } else if (Array.isArray(property.type)) {
        // TODO: Remove this when ready - Backwards compatibility for ["string", "number"]

        const formControl = this.buildUnionArrayFormControl(property.type, fullKey);

        if (property._ui_disable) {
          formControl.disable();
        }

        baseForm.addControl(key, formControl);
      } else {
        switch (property.type) {
          case 'array': {
            if (!property.items) {
              console.error(
                `The property ${fullKey} is an array type and must have some items.\nProperty:\n${property}`,
              );
              break;
            }

            if (
              property?.[SchemaKeywords.UiWidget] === SfoUiWidgetType.EnumInput &&
              property?.[SchemaKeywords.UiWidget]
            ) {
              baseForm.addControl(key, this.formBuilder.control(undefined));
            } else {
              baseForm.addControl(
                key,
                this.buildFormArray(
                  property,
                  fullKey,
                  (defaults as any)?.[key] || // access default value of an object, passed down from root
                    defaults || // access default value of primitive, passed from root
                    (property?.default as unknown) || // access default at root level
                    undefined,
                ),
              );
            }
            break;
          }
          case 'object': {
            const newFormControl = this.buildFormGroup(
              property,
              fullKey,
              (defaults as any)?.[key] || // access default value of an object, passed down from root
                defaults || // access default value of primitive, passed from root
                (property?.default as unknown) || // access default at root level
                undefined,
            );

            baseForm.addControl(key, newFormControl);
            break;
          }
          case 'number': {
            const validationRules = buildNumberValidators(property);
            const formControl = this.formBuilder.control(undefined, validationRules);
            baseForm.addControl(key, formControl);
            break;
          }
          case 'string': {
            const validationRules = buildStringValidators(property);
            const formControl = this.formBuilder.control(undefined, validationRules);

            if (property?._ui_disable) {
              formControl.disable();
            }

            baseForm.addControl(key, formControl);

            break;
          }
          // Special: if widget is DateVariable but schema does not expose type (handled via anyOf)
          // fall back to a control that holds [string]
          case 'boolean': {
            const defaultValue =
              (defaults as any)?.[key] ?? // access default value from passed defaults object
              (property?.default as unknown) ?? // access default from schema
              undefined;
            baseForm.addControl(key, this.formBuilder.control(defaultValue));
            break;
          }
          default:
            throw new Error(
              `Building form failed. Path "${fullKey}" has an unsupported type "${property.type}".`,
            );
        }
      }
    }
    return baseForm;
  }

  buildFormControl(schema: any): FormControl {
    const validators: ValidatorFn[] = buildStringValidators(schema);
    return new FormControl('', validators);
  }

  /**
   * Gets the full path of a control in the form hierarchy
   * @param form - The form group containing the control
   * @param key - The control key to find the path for
   * @returns The full dot-notation path to the control
   */
  getControlPath(form: FormGroup, key: string): string {
    let path = key;
    let current: AbstractControl | null = form;

    while (current?.parent) {
      const parent = current.parent;

      if (parent instanceof FormGroup) {
        const parentKey = Object.keys(parent.controls).find((k) => parent.get(k) === current);
        if (!parentKey) {
          break;
        }
        path = `${parentKey}.${path}`;
        current = parent;
        continue;
      }

      if (parent instanceof FormArray) {
        const arrayParent = parent.parent;
        if (arrayParent instanceof FormGroup) {
          const arrayKey = Object.keys(arrayParent.controls).find(
            (k) => arrayParent.get(k) === parent,
          );
          if (!arrayKey) {
            break;
          }
          path = `${arrayKey}.items.${path}`;
          current = arrayParent;
          continue;
        }

        // Handle nested arrays
        if (arrayParent instanceof FormArray) {
          path = `items.${path}`;
          current = arrayParent;
          continue;
        }

        break;
      }

      break;
    }

    return path;
  }

  public buildFormArray(
    property: SfoUiJSONSchema7,
    fullKey: string,
    schemaDefaults?: unknown,
  ): FormArray {
    const formArray = this.formBuilder.array<FormGroup | FormControl>([]);

    // Resolve item schema, supporting object unions (anyOf/oneOf)
    const itemSchema: SfoUiJSONSchema7 | undefined = property?.items as
      | SfoUiJSONSchema7
      | undefined;
    const resolvedObjectSchema = itemSchema ? resolveObjectSchemaForService(itemSchema) : null;

    if (resolvedObjectSchema) {
      // If there are default properties that live on the root level of the schema object
      if (Array.isArray(property.default)) {
        property.default.forEach((defaultValue: any, idx: number) => {
          // pick best-fitting variant for the default value
          const picked =
            pickObjectVariantForValue(itemSchema!, defaultValue) || resolvedObjectSchema;
          formArray.push(this.buildFormGroup(picked, `${fullKey}[${idx}]`, defaultValue));
        });
        // collect defaults parsed from the root level
      } else if (Array.isArray(schemaDefaults)) {
        // build form with the defaults living on the root level
        (schemaDefaults as unknown[]).forEach((defaultValue: unknown, idx: number) => {
          const picked =
            pickObjectVariantForValue(itemSchema!, defaultValue) || resolvedObjectSchema;
          formArray.push(this.buildFormGroup(picked, `${fullKey}[${idx}]`, defaultValue));
        });
      } else if (
        property?.[SchemaKeywords.UiWidget] !== SfoUiWidgetType.CreatorRoleArray
      ) {
        // No defaults: pick first object variant. Creator-role arrays stay
        // empty (header + Add only) — a forced placeholder row would serialize
        // as `[{}]` and read as a contributor the reference doesn't have.
        formArray.push(this.buildFormGroup(resolvedObjectSchema, `${fullKey}[0]`));
      }
    } else {
      // Primitive or non-object arrays. Prefer provided defaults (schemaDefaults)
      const defaultsFromSchema: unknown[] = Array.isArray(property.default)
        ? (property.default as unknown[])
        : [];
      const defaultsFromParam: unknown[] = Array.isArray(schemaDefaults)
        ? (schemaDefaults as unknown[])
        : [];
      const defaults: unknown[] = defaultsFromParam.length ? defaultsFromParam : defaultsFromSchema;

      defaults.forEach((defaultValue) => {
        formArray.push(this.formBuilder.control(defaultValue));
      });
    }

    return formArray;
  }

  /**
   * Builds a form control for properties with anyOf or oneOf definitions in the schema.
   * This handles complex unions like:
   * - Array combined with null: an array that can also be null
   * - Simple type unions: string, number, or null in any combination
   *
   * @param property - The schema property containing anyOf or oneOf
   * @param key - The property key for error messages
   * @returns A FormControl for simple types or FormArray for array types
   * @throws Error if the union type combination is not supported
   */
  private buildUnionFormControl(
    property: SfoUiJSONSchema7,
    key: string,
  ): FormControl | FormArray | FormGroup {
    const typeSetMixed = (property.anyOf || property.oneOf) as
      | (SfoUiJSONSchema7 | boolean)[]
      | undefined;
    if (!typeSetMixed) {
      throw new Error(`Property "${key}" must have anyOf or oneOf defined`);
    }

    const typeSet = typeSetMixed.filter(
      (v): v is SfoUiJSONSchema7 => typeof v === 'object' && v !== null,
    );
    const types = typeSet.map((option) => option?.type).filter(Boolean) as string[];

    if (types.includes('array')) {
      const defaultToNull = property.examples?.find((ex) => (ex as any)?.value === null);
      if (defaultToNull) {
        return this.formBuilder.control(null);
      }

      const arraySchemaType = typeSet.find((option) => option?.type === 'array');
      if (!arraySchemaType || !arraySchemaType.items) {
        return this.formBuilder.array([]);
      }

      return this.buildFormArray(arraySchemaType, key, property.default);
    }

    // No widget override here; unions are resolved structurally.

    // Support unions that include an object schema (e.g., CSL date model)
    if (types.includes('object')) {
      const objectSchema = typeSet.find((option) => option?.type === 'object');
      if (!objectSchema) {
        throw new Error(`Union for "${key}" claims object type but no schema provided.`);
      }

      // If a default is provided for the union, pass it down so nested fields can use it
      return this.buildFormGroup(
        objectSchema as SfoUiJSONSchema7,
        key,
        (property?.default as unknown) || undefined,
      );
    }

    const validSimpleTypes = ['string', 'number', 'boolean', 'integer', 'null'];
    const isSimpleTypeUnion = types.every((type) => validSimpleTypes.includes(type));

    if (isSimpleTypeUnion) {
      return this.formBuilder.control<string | number | boolean | null | undefined>(undefined);
    }

    throw new Error(
      `Unsupported anyOf/oneOf types for property: "${key}". Currently supporting object, simple types (string/number/null), or array combinations.`,
    );
  }

  /**
   * Builds a form control for properties with an array type definition.
   * Simple type unions like ["string", "number"] are generated.
   * https://github.com/YousefED/typescript-json-schema/blob/master/test/programs/type-union/main.ts
   *
   * @param property - The array of types from the schema
   * @param key - The property key for error messages
   * @returns A FormControl configured for the union type
   * @throws Error if the array contains unsupported types
   */
  private buildUnionArrayFormControl(property: string[], key: string): FormControl {
    // Allow common simple unions defined as an array of types
    const allowed = new Set(['string', 'number', 'boolean', 'null']);

    const hasOnlyAllowed = property.every((t) => allowed.has(t));
    if (!hasOnlyAllowed) {
      throw new Error(
        `Unsupported union for ${key}. Contains ${property}. Allowed: string,number,boolean,null`,
      );
    }

    // Return a generic control that can hold any of the allowed types
    return this.formBuilder.control<string | number | boolean | null | undefined>(undefined);
  }
}
